import { datadogLogs } from '@datadog/browser-logs';
import { matchPath } from 'react-router-dom';
import menu, { service } from 'config/menuList';
import storage from './storage';
import beforeSendHandler from './datadogBeforeSend';

const CLIENT_TOKEN = 'pub557b867889d5752f51c0b6bed690fbc8';
const LOG_VERSION = '0.0.1';

const DATADOG_ENV_MAP: Record<string, string> = {
  production: 'production',
  'production-stage': 'development',
  'production-test': 'development'
};

const datadogEnv = DATADOG_ENV_MAP[process.env.ENV as string];

type ScreenEntry = { path: string; screen: string; key: string };

// '/main/staffManagement' → 'MAIN_STAFF_MANAGEMENT'
const toKey = (path: string) =>
  path
    .replace(/^\//, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[/-]/g, '_')
    .toUpperCase();

// LNB 정의를 화면 목록으로 펼친다. 경로가 없는 그룹 항목과 외부 링크는 제외된다.
const collectMenu = (nodes: any[], acc: ScreenEntry[]) => {
  (nodes || []).forEach((node) => {
    if (node.url && node.url.charAt(0) === '/') {
      acc.push({ path: node.url, screen: node.name, key: toKey(node.url) });
    }
    collectMenu(node.depth2, acc);
    collectMenu(node.depth3, acc);
  });
};

const collectService = (nodes: any[], acc: ScreenEntry[]) => {
  (nodes || []).forEach((node) => {
    if (node.to && node.to.charAt(0) === '/') {
      acc.push({ path: node.to, screen: node.title, key: toKey(node.to) });
    }
    collectService(node.children, acc);
  });
};

const buildScreenMap = (): ScreenEntry[] => {
  const acc: ScreenEntry[] = [];
  collectMenu((menu as any)?.depth1, acc);
  const svc = service as any;
  Object.keys(svc || {}).forEach((key) => collectService(svc[key], acc));

  const byPath: Record<string, ScreenEntry> = {};
  acc.forEach((entry) => {
    if (!byPath[entry.path]) byPath[entry.path] = entry;
  });
  // 긴 경로부터 검사해 가장 구체적인 화면이 먼저 매칭되게 한다.
  return Object.keys(byPath)
    .map((path) => byPath[path])
    .sort((a, b) => b.path.length - a.path.length);
};

const SCREEN_MAP = buildScreenMap();

// 라우터가 v4.1.1 이라 v6 의 matchRoutes 를 쓸 수 없다.
// exact 를 끄면 상세 라우트(/main/staffManagement/123)도 부모 화면으로 잡힌다.
const getScreenInfo = (): Partial<ScreenEntry> =>
  SCREEN_MAP.filter((entry) => matchPath(window.location.pathname, { path: entry.path, exact: false }))[0] || {};

const buildLogContext = (messageContext?: unknown) => {
  const { key, screen } = getScreenInfo();
  return { ...(messageContext as object), 'ui-key': key, 'ui-screen': screen, 'log-version': LOG_VERSION };
};

export const datadogLogsInit = () => {
  if (!datadogEnv) return;
  datadogLogs.init({
    clientToken: CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: `${datadogEnv}-corp-web`,
    env: datadogEnv,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    beforeSend: beforeSendHandler
  });
};

export const setUser = () => {
  if (!datadogEnv) return;
  const company = storage.get('company');
  if (!company) return;
  const { user } = company;
  if (!user) return;

  datadogLogs.setUser({
    id: user.id,
    name: user.name,
    companyId: company.id
  });
};

export const clearUser = () => {
  if (!datadogEnv) return;
  datadogLogs.clearUser();
};

export const infoLogger = (message: string, messageContext?: unknown) => {
  if (!datadogEnv) return;
  datadogLogs.logger.info(message, buildLogContext(messageContext));
};

export const errorLogger = (message: string, messageContext?: unknown, error?: Error) => {
  if (!datadogEnv) return;
  datadogLogs.logger.error(message, buildLogContext(messageContext), error);
};

export const eventLogger = (message: string, messageContext?: unknown) => {
  if (!datadogEnv) return;
  datadogLogs.logger.info(message, { ...buildLogContext(messageContext), 'ui-metric': true });
};
